import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../lib/cn";
import { nx, NX_RING_CLIP, type NxSize } from "../lib/onyx-material";

const buttonVariants = cva(
  "relative z-[1] inline-flex shrink-0 select-none items-center justify-center gap-2 text-sm font-medium whitespace-nowrap transition-transform duration-150 ease-[cubic-bezier(0.34,1.45,0.6,1)] active:translate-y-px outline-none focus-visible:[filter:drop-shadow(0_0_1px_hsl(var(--ring)))_drop-shadow(0_0_3px_hsl(var(--ring)/0.55))] disabled:pointer-events-none disabled:opacity-50 disabled:active:translate-y-0 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-white hover:bg-destructive/90",
        outline: "bg-background hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:hover:bg-input/50",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-xs": "size-6 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

/**
 * Stock shadcn button — in the Onyx notch language.
 *
 * Same 6 variants (`default`, `destructive`, `outline`, `secondary`, `ghost`,
 * `link`) + 8 sizes as upstream, but the box is the hex-notch silhouette (`nx`
 * `clip-path`, not `rounded-md`) with a continuous `nx-rim` keyline ring (a real
 * border is sliced at the notch, #158) and the shared springy press — a 1px
 * press-DOWN on `:active` with the overshoot bounce every Button uses. The
 * brand `Button` (the lib default export) carries the full offset-key depth (a
 * travel block); this stock escape-hatch stays lighter: notch + keyline + press.
 *
 * `link` is the one non-key variant (inline text, no notch/ring/press). Use
 * `asChild` to render as your own element (a router Link, a Radix trigger).
 *
 * @example
 *   <Button variant="outline" size="sm">Save</Button>
 *   <Button asChild><Link to="/x">Go</Link></Button>
 */
function Button({
  className,
  variant = "default",
  size = "default",
  notch = "b",
  asChild = false,
  children,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /** Notch chamfer size. Default `b`. */
    notch?: NxSize;
  }) {
  const Comp = asChild ? Slot : "button";
  const isLink = variant === "link";
  const isGhost = variant === "ghost";

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(
        !isLink && nx[notch],
        buttonVariants({ variant, size }),
        // Inline link is not a key: drop the box height + the press.
        isLink && "h-auto px-1 active:translate-y-0",
        className,
      )}
      {...props}
    >
      {/* Notched keyline ring — traces the notch where a real border is sliced
          (#158). From --foreground so it stays neutral. Ghost + link have none. */}
      {!isGhost && !isLink && (
        <span
          aria-hidden
          className="nx-rim pointer-events-none absolute inset-0"
          style={{ "--nx-rim": "hsl(var(--border))", clipPath: NX_RING_CLIP[notch] } as React.CSSProperties}
        />
      )}
      <Slottable>{children}</Slottable>
    </Comp>
  );
}

export { Button, buttonVariants };
